import { useCallback, useEffect, useRef, useState } from 'react'
import {
  isMuted,
  playTone,
  randomFrequency,
  scorePitch,
  setMuted,
  sfxSubmit,
  startLiveTone,
  stopLiveTone,
  unlockAudio,
  updateLiveTone,
} from '../gameslib/audio'
import { SOUND_LISTEN_SECONDS } from '../gameslib/difficulty'
import { useGameSettings } from '../gameslib/gameSettings'
import {
  createNoise,
  drawStrandsVertical,
  setupCanvas,
} from '../gameslib/soundWave'
import { Countdown, MemoryTimer } from '../gamesshared/Countdown'
import { ArrowIcon, GameShell, IntroScreen, PlayerBanner } from '../gamesshared/IntroScreen'
import { PassDevice, PlayerSetup, WinnerScreen } from '../gamesshared/Multiplayer'
import { RoundResult, TotalScreen } from '../gamesshared/RoundResult'
import { TutorialCoach } from '../gamesshared/TutorialCoach'
import { useTutorial } from '../gameslib/useTutorial'

const MIN_HZ = 200
const MAX_HZ = 900

function hzToNorm(hz) {
  const lo = Math.log(MIN_HZ)
  const hi = Math.log(MAX_HZ)
  return Math.min(1, Math.max(0, (Math.log(hz) - lo) / (hi - lo)))
}

function WaveCanvas({ hz, dragIntensity = 0 }) {
  const backRef = useRef(null)
  const frontRef = useRef(null)
  const hzRef = useRef(hz)
  const dragRef = useRef(dragIntensity)
  hzRef.current = hz
  dragRef.current = dragIntensity

  useEffect(() => {
    const noise = createNoise()
    const t0 = performance.now()
    let raf = 0

    const draw = () => {
      const t = (performance.now() - t0) / 1000
      const norm = hzToNorm(hzRef.current)
      const drag = dragRef.current
      const back = setupCanvas(backRef.current)
      const front = setupCanvas(frontRef.current)
      if (back && front) {
        drawStrandsVertical(noise, back.ctx, back.w, back.h, norm, t, 1, 5, 1, drag, 'mixed')
        drawStrandsVertical(noise, front.ctx, front.w, front.h, norm, t, 1, 1.1, 1, drag, 'mixed')
      }
      raf = requestAnimationFrame(draw)
    }

    raf = requestAnimationFrame(draw)
    return () => cancelAnimationFrame(raf)
  }, [])

  return (
    <div className="wave-canvas" aria-hidden>
      <canvas ref={backRef} className="wave-canvas__back" />
      <canvas ref={frontRef} className="wave-canvas__front" />
    </div>
  )
}

function makeTargets(count) {
  return Array.from({ length: count }, () => randomFrequency(MIN_HZ, MAX_HZ))
}

export function SoundGame() {
  const { rounds, setRounds, setModeLabel } = useGameSettings()
  const tutorial = useTutorial('sound')
  const [phase, setPhase] = useState('intro')
  const [difficulty, setDifficulty] = useState('easy')
  const [round, setRound] = useState(0)
  const [targets, setTargets] = useState([])
  const [guess, setGuess] = useState(440)
  const [scores, setScores] = useState([])
  const [lastScore, setLastScore] = useState(0)
  const [dragIntensity, setDragIntensity] = useState(0)
  const stripRef = useRef(null)
  const submitRef = useRef(null)
  const nextRef = useRef(null)
  const dragTrack = useRef({ y: 0, t: 0, active: false })
  const startGuess = useRef(440)

  const [playMode, setPlayMode] = useState('solo')
  const [players, setPlayers] = useState([])
  const [playerIndex, setPlayerIndex] = useState(0)
  const [soundRequired, setSoundRequired] = useState(false)
  const pendingStart = useRef(null)

  const target = targets[round]
  const currentPlayer = players[playerIndex]
  const playRounds = tutorial.active ? 1 : rounds
  const listenS = tutorial.active ? Math.max(SOUND_LISTEN_SECONDS.easy, 2.2) : SOUND_LISTEN_SECONDS[difficulty]

  useEffect(() => {
    setModeLabel(playMode === 'multi' ? 'multi' : 'solo')
  }, [playMode, setModeLabel])

  useEffect(() => {
    if (phase !== 'input') setDragIntensity(0)
  }, [phase])

  useEffect(() => {
    if (phase === 'intro' || phase === 'setup' || phase === 'pass') return undefined
    const check = () => {
      if (isMuted()) {
        stopLiveTone()
        setSoundRequired(true)
      }
    }
    check()
    const id = window.setInterval(check, 400)
    return () => window.clearInterval(id)
  }, [phase])

  const resetTurn = (shared = targets) => {
    setScores([])
    setRound(0)
    setGuess(440)
    setLastScore(0)
    stopLiveTone()
    if (shared?.length) setTargets(shared)
  }

  const beginPlay = (asTutorial) => {
    void unlockAudio()
    setPlayMode('solo')
    setPlayers([])
    setPlayerIndex(0)
    if (asTutorial) tutorial.start('listen')
    else if (tutorial.active) tutorial.finish()
    const next = makeTargets(asTutorial ? 1 : rounds)
    setTargets(next)
    resetTurn(next)
    setPhase('countdown')
  }

  const requireSound = (then) => {
    if (isMuted()) {
      pendingStart.current = then
      setSoundRequired(true)
      return false
    }
    return true
  }

  const enableSound = () => {
    setMuted(false)
    void unlockAudio()
    setSoundRequired(false)
    const next = pendingStart.current
    pendingStart.current = null
    if (typeof next === 'function') next()
  }

  const dismissSoundRequired = () => {
    pendingStart.current = null
    setSoundRequired(false)
    stopLiveTone()
    if (phase !== 'intro' && phase !== 'setup' && phase !== 'pass') {
      if (tutorial.active) tutorial.skip()
      setPhase('intro')
      setPlayMode('solo')
      setPlayers([])
      setPlayerIndex(0)
    }
  }

  const startSolo = () => {
    const run = () => beginPlay(tutorial.needsTutorial)
    if (!requireSound(run)) return
    run()
  }
  const startTeach = () => {
    const run = () => beginPlay(true)
    if (!requireSound(run)) return
    run()
  }

  const exitTutorial = () => {
    stopLiveTone()
    tutorial.skip()
    setPhase('intro')
    setPlayMode('solo')
    setPlayers([])
    setPlayerIndex(0)
  }

  const confirmPlayers = (list) => {
    const run = () => {
      void unlockAudio()
      if (tutorial.active) tutorial.finish()
      const next = makeTargets(rounds)
      setTargets(next)
      setPlayers(list.map((p) => ({ ...p, scores: [] })))
      setPlayerIndex(0)
      setPlayMode('multi')
      resetTurn(next)
      setPhase('pass')
    }
    if (!requireSound(run)) return
    run()
  }

  const finishPlayerTurn = (finalScores) => {
    if (playMode === 'solo') {
      setPhase('total')
      return
    }
    const updated = players.map((p, i) => (i === playerIndex ? { ...p, scores: finalScores } : p))
    setPlayers(updated)
    if (playerIndex >= players.length - 1) setPhase('winner')
    else {
      setPlayerIndex((i) => i + 1)
      setPhase('pass')
    }
  }

  const onCountdownDone = useCallback(() => {
    if (tutorial.active) tutorial.go('listen')
    setPhase('stimulus')
  }, [tutorial])

  const onStimulusDone = useCallback(() => {
    if (tutorial.active) tutorial.go('drag')
    setPhase('input')
  }, [tutorial])

  useEffect(() => {
    if (phase === 'stimulus' && target) {
      stopLiveTone()
      void (async () => {
        await unlockAudio()
        await playTone(target, listenS * 1000)
      })()
    }
  }, [phase, target, listenS])

  useEffect(() => {
    if (phase === 'input') {
      startGuess.current = guess
      void (async () => {
        await unlockAudio()
        await startLiveTone(guess, 0.14)
      })()
      return () => stopLiveTone()
    }
    stopLiveTone()
    return undefined
  }, [phase]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (phase === 'input') updateLiveTone(guess)
  }, [guess, phase])

  const setFromY = (clientY) => {
    const el = stripRef.current
    if (!el) return
    const rect = el.getBoundingClientRect()
    const t = Math.min(1, Math.max(0, (clientY - rect.top) / rect.height))
    const lo = Math.log(MIN_HZ)
    const hi = Math.log(MAX_HZ)
    const next = Math.exp(hi - t * (hi - lo))
    setGuess(next)
    if (tutorial.active && tutorial.step === 'drag' && Math.abs(next - startGuess.current) > 12) {
      tutorial.go('submit')
    }
  }

  const onStripDown = (e) => {
    e.currentTarget.setPointerCapture(e.pointerId)
    dragTrack.current = { y: e.clientY, t: performance.now(), active: true }
    setFromY(e.clientY)
  }

  const onStripMove = (e) => {
    if (!e.currentTarget.hasPointerCapture(e.pointerId)) return
    const now = performance.now()
    const dt = Math.min(50, Math.max(8, now - dragTrack.current.t))
    const dy = Math.abs(e.clientY - dragTrack.current.y)
    dragTrack.current = { y: e.clientY, t: now, active: true }
    const intensity = Math.min(1, (dy / dt) * 0.45)
    setDragIntensity((prev) => prev * 0.65 + intensity * 0.35)
    setFromY(e.clientY)
  }

  const onStripUp = () => {
    dragTrack.current.active = false
    setDragIntensity(0)
  }

  const submit = () => {
    if (tutorial.active && tutorial.step === 'drag') return
    stopLiveTone()
    void sfxSubmit()
    const s = scorePitch(target, guess)
    setLastScore(s)
    setScores((prev) => [...prev, s])
    if (tutorial.active) tutorial.go('done')
    setPhase('result')
  }

  const next = () => {
    if (tutorial.active) {
      tutorial.finish()
      setPhase('intro')
      return
    }
    if (round + 1 >= playRounds) {
      const finalScores = scores.length >= playRounds ? scores : [...scores, lastScore]
      finishPlayerTurn(finalScores)
    } else {
      setRound((r) => r + 1)
      setGuess(440)
      setPhase('countdown')
    }
  }

  const coach =
    tutorial.active &&
    ({
      listen: { message: 'Listen to the tone', gesture: 'listen', anchor: 'center' },
      drag: { message: 'Drag to match pitch', gesture: 'drag', anchor: 'right' },
      submit: { message: 'Tap to submit', gesture: 'tap', anchor: 'submit' },
      done: { message: 'Closer Hz scores higher', gesture: 'tap', anchor: 'submit' },
    }[tutorial.step] || null)
  const coachTarget =
    tutorial.step === 'done'
      ? nextRef
      : tutorial.step === 'drag'
        ? stripRef
        : tutorial.step === 'submit'
          ? submitRef
          : null

  return (
    <GameShell>
      {soundRequired && (
        <div className="sound-required" role="dialog" aria-modal="true" aria-labelledby="sound-required-title">
          <div className="sound-required__card">
            <span className="material-symbols-outlined sound-required__icon" aria-hidden>
              volume_off
            </span>
            <h2 id="sound-required-title" className="sound-required__title">
              Sound is required
            </h2>
            <p className="sound-required__body">
              This game needs audio on so you can hear and match each tone. Turn sound on to continue.
            </p>
            <button type="button" className="sound-required__btn" onClick={enableSound}>
              Turn sound on
            </button>
            <button type="button" className="sound-required__link" onClick={dismissSoundRequired}>
              Back
            </button>
          </div>
        </div>
      )}

      {phase === 'intro' && (
        <IntroScreen
          title="sound"
          blurb={`Humans can't reliably recall pitches. This is a simple game to see how good (or bad) you are at it.\nWe'll play ${rounds} tone${rounds === 1 ? '' : 's'}, then you'll try and recreate each one.`}
          onSolo={startSolo}
          onMulti={() => {
            if (!requireSound(() => setPhase('setup'))) return
            setPhase('setup')
          }}
          onTeach={startTeach}
          difficulty={difficulty}
          onDifficulty={setDifficulty}
          rounds={rounds}
          onRounds={setRounds}
        />
      )}

      {phase === 'setup' && (
        <PlayerSetup onConfirm={confirmPlayers} onBack={() => setPhase('intro')} rounds={rounds} onRounds={setRounds} />
      )}

      {phase === 'pass' && currentPlayer && (
        <PassDevice
          player={currentPlayer}
          index={playerIndex}
          total={players.length}
          rounds={rounds}
          onReady={() => {
            const run = () => {
              resetTurn(targets)
              setPhase('countdown')
            }
            if (!requireSound(run)) return
            run()
          }}
        />
      )}

      {phase === 'countdown' && (
        <div className="stage">
          {playMode === 'multi' && <PlayerBanner name={currentPlayer?.name} />}
          {!tutorial.active && (
            <div className="round-pill">
              {round + 1} / {playRounds}
            </div>
          )}
          <Countdown onDone={onCountdownDone} />
          {coach && <TutorialCoach {...coach} onSkip={exitTutorial} />}
        </div>
      )}

      {phase === 'stimulus' && (
        <div className="sound-stage">
          {playMode === 'multi' && <PlayerBanner name={currentPlayer?.name} />}
          {!tutorial.active && (
            <div className="round-pill">
              {round + 1} / {playRounds}
            </div>
          )}
          <WaveCanvas hz={target} />
          <MemoryTimer seconds={listenS} label="Listen closely" onDone={onStimulusDone} />
          <div className="watermark">Memory</div>
          {coach && <TutorialCoach {...coach} onSkip={exitTutorial} />}
        </div>
      )}

      {phase === 'input' && (
        <div className="sound-stage">
          {playMode === 'multi' && <PlayerBanner name={currentPlayer?.name} />}
          {!tutorial.active && (
            <div className="round-pill">
              {round + 1} / {playRounds}
            </div>
          )}
          <WaveCanvas hz={guess} dragIntensity={dragIntensity} />
          <div
            ref={stripRef}
            className={`pitch-strip${tutorial.step === 'drag' ? ' is-tutor-pulse' : ''}`}
            role="slider"
            aria-label="Frequency"
            onPointerDown={onStripDown}
            onPointerMove={onStripMove}
            onPointerUp={onStripUp}
            onPointerCancel={onStripUp}
          />
          <div className="hz-readout">
            <span className="hz-readout__num">{guess.toFixed(2)}</span>
            <span className="hz-readout__unit">Hz</span>
          </div>
          <div className="watermark">Memory</div>
          <button
            type="button"
            ref={submitRef}
            className={`go-btn${tutorial.step === 'submit' ? ' is-tutor-pulse' : ''}`}
            aria-label="Submit"
            onClick={submit}
            disabled={tutorial.active && tutorial.step === 'drag'}
          >
            <ArrowIcon />
          </button>
          {coach && <TutorialCoach {...coach} onSkip={exitTutorial} targetRef={coachTarget} />}
        </div>
      )}

      {phase === 'result' && (
        <div className="screen">
          <RoundResult
            score={lastScore}
            round={tutorial.active ? 1 : round + 1}
            totalRounds={playRounds}
            onNext={next}
            nextButtonRef={nextRef}
            scoreColor="#fff"
            layout="stack"
          >
            <div className="pitch-compare">
              <p>
                You: <strong>{guess.toFixed(1)} Hz</strong>
              </p>
              <p>
                Target: <strong>{target.toFixed(1)} Hz</strong>
              </p>
            </div>
          </RoundResult>
          {coach && <TutorialCoach {...coach} onSkip={exitTutorial} targetRef={coachTarget} />}
        </div>
      )}

      {phase === 'total' && <TotalScreen scores={scores} onReplay={startSolo} />}

      {phase === 'winner' && (
        <WinnerScreen
          players={players}
          onReplay={() => {
            const nextTargets = makeTargets(rounds)
            setTargets(nextTargets)
            setPlayers((prev) => prev.map((p) => ({ ...p, scores: [] })))
            setPlayerIndex(0)
            resetTurn(nextTargets)
            setPhase('pass')
          }}
          onHome={() => {
            setPhase('intro')
            setPlayMode('solo')
            setPlayers([])
            setPlayerIndex(0)
          }}
        />
      )}
    </GameShell>
  )
}
